// MeetingTimeConversionPanel — the FS-TIME-5 / FS-MTG-2 "convert attendance to time" step.
//
// Shared by BOTH surfaces that offer the conversion so behavior is identical:
//   - the standalone /meetings/:id/convert-to-time page (MeetingConvertToTimePage)
//   - the Meeting Detail "Time" section (/meetings/:id?section=time)
//
// Per AC-MTG-2.4: suggested hours are EDITABLE per attendee before creation, the
// destination week each draft lands in is shown up front, and success and failure
// both confirm (toasts) with links to the weekly timesheet surface.
//
// Import directly by path (not via components/index.js) to keep this additive:
//   import { MeetingTimeConversionPanel } from '../time/MeetingTimeConversionPanel.jsx';

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/apiClient.js';
import { useMutation } from '../../lib/useApi.js';
import { BrandedEmptyState, NumberInput, toast } from '../../components/index.js';
import { usePeople } from '../../lib/usePeople.js';
import { PersonLabel } from '../../components/primitives/PersonLabel.jsx';
import { todayDateOnly, weekPeriodForDate } from './timeHelpers.js';
import './time.css';
import './timesheets.redesign.css';

function fmtDraftHours(value) {
  const rounded = Math.round(Number(value || 0) * 100) / 100;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
}

export function MeetingTimeConversionPanel({ meeting }) {
  const { nameOf } = usePeople();
  const mutation = useMutation();
  const [drafts, setDrafts] = useState([]);
  // Per-attendee hour edits; attendees without an edit keep the 1-hour default.
  const [hoursByUser, setHoursByUser] = useState({});

  const attendees = Array.isArray(meeting?.attendees) ? meeting.attendees : [];
  // Destination week: the weekly period covering the meeting date (fallback: today).
  const destination = weekPeriodForDate(meeting?.startAt || todayDateOnly());
  const destinationLabel =
    destination.periodStart && destination.periodEnd
      ? `${destination.periodStart} to ${destination.periodEnd}`
      : 'current week';

  function hoursFor(userId) {
    const value = hoursByUser[userId];
    return value === undefined || value === null ? 1 : value;
  }

  async function createDrafts() {
    if (!meeting?.id) return;
    const payloadHours = Object.fromEntries(
      attendees.map((userId) => [userId, Number(hoursFor(userId)) || 1]),
    );
    try {
      const response = await mutation.run(() =>
        api.post(`/meetings/${meeting.id}/time-drafts`, {
          suggestedHours: 1,
          hoursByUser: payloadHours,
        }),
      );
      const list = response?.data?.drafts || response?.data?.data?.drafts || [];
      setDrafts(list);
      toast.success({
        title: 'Draft time entries created',
        body: list.length
          ? `${list.length} draft${list.length === 1 ? '' : 's'} suggested from attendance for the week of ${destinationLabel}.`
          : 'No new drafts — every attendee already has a suggestion for this meeting.',
      });
    } catch (error) {
      toast.error({ title: 'Could not create draft entries', body: error.message });
    }
  }

  const draftHours = drafts.reduce((total, d) => total + (Number(d.suggestedHours) || 0), 0);

  return (
    <section className="ts-bento" data-testid="meeting-time-conversion">
      <article className="time-panel">
        <div className="ts-panel-head">
          <h3>Convert attendance to time</h3>
          <span className="chip outline sm">{attendees.length}</span>
        </div>
        {attendees.length ? (
          <>
            <p className="muted ts-convert-hint">
              Suggested hours are editable per attendee. Drafts land in the weekly timesheet
              covering <strong>{destinationLabel}</strong>.
            </p>
            <ul className="time-list ts-people-list ts-convert-list">
              {attendees.map((attendee) => (
                <li key={attendee}>
                  <span className="ts-people-avatar" aria-hidden>
                    {String(nameOf(attendee))
                      .replace(/[^A-Za-z0-9]/g, '')
                      .slice(-2)
                      .toUpperCase() || '··'}
                  </span>
                  <span className="ts-convert-person">
                    <PersonLabel userId={attendee} />
                  </span>
                  <span className="ts-convert-hours">
                    <label className="ts-sr-only" htmlFor={`mtc-hours-${attendee}`}>
                      Hours for {nameOf(attendee)}
                    </label>
                    <NumberInput
                      id={`mtc-hours-${attendee}`}
                      value={hoursFor(attendee)}
                      onChange={(value) =>
                        setHoursByUser((current) => ({ ...current, [attendee]: value }))
                      }
                      min={0.25}
                      step={0.25}
                      suffix="h"
                      disabled={mutation.running}
                    />
                  </span>
                </li>
              ))}
            </ul>
            <div className="row" style={{ justifyContent: 'flex-end', marginTop: 12 }}>
              <button
                type="button"
                className="btn primary"
                onClick={createDrafts}
                disabled={mutation.running}
              >
                {mutation.running ? 'Creating…' : 'Create draft time entries'}
              </button>
            </div>
          </>
        ) : (
          <BrandedEmptyState
            icon="👥"
            eyebrow="OWHA Time Operations"
            title="No attendees"
            description="Meeting attendance is required for time-draft conversion."
            tone="slate"
          />
        )}
      </article>
      <article className="time-panel">
        <div className="ts-panel-head">
          <h3>Draft time entries</h3>
          {drafts.length ? (
            <span className="ts-panel-sub">{fmtDraftHours(draftHours)} hrs drafted</span>
          ) : null}
        </div>
        {drafts.length ? (
          <>
            <ul className="time-list ts-draft-list">
              {drafts.map((draft) => (
                <li key={draft.id}>
                  <span className="ts-people-avatar" aria-hidden>
                    {String(nameOf(draft.userId))
                      .replace(/[^A-Za-z0-9]/g, '')
                      .slice(-2)
                      .toUpperCase() || '··'}
                  </span>
                  <span>
                    <strong>
                      <PersonLabel userId={draft.userId} />
                    </strong>{' '}
                    · {draft.suggestedHours} hours · week of {destinationLabel}
                  </span>
                </li>
              ))}
            </ul>
            <p className="muted ts-convert-hint">
              Drafts are suggestions for each attendee&apos;s weekly record.{' '}
              <Link to="/timesheets/current">Open the current weekly timesheet</Link>
            </p>
          </>
        ) : (
          <p className="muted">No draft time entries created yet.</p>
        )}
      </article>
    </section>
  );
}
